import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A single character matches thousands of products and says nothing about intent, so it
 * costs three requests to render noise. Two is the shortest useful query on this
 * catalogue (product codes like 'T5' are real searches).
 */
const MIN_SEARCH_LENGTH = 2;
const SKELETON_PILLS = 8;
const SKELETON_CARDS = 8;

/**
 * Do Algolia's cards actually contain the words that were typed?
 *
 * Algolia is typo tolerant, so a query it cannot match is answered with near-misses
 * ('frog' comes back as 'frosted'). When that happens Shopify's own answer is the better
 * one, so nothing is painted early and Algolia's cards go after Shopify's rather than
 * before them.
 *
 * @param {HTMLElement[]} items
 * @param {string} searchTerm
 * @returns {boolean}
 */
function isOnTopic(items, searchTerm) {
  const words = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
  return items.some((li) => {
    const text = li.textContent?.toLowerCase() ?? '';
    return words.every((word) => text.includes(word));
  });
}

/**
 * Build a placeholder panel shaped like real results.
 *
 * Shown only when there is nothing to keep on screen, i.e. the shopper's first search.
 * Without it the dropdown sits completely unchanged while the requests are in flight,
 * which reads as broken rather than as loading.
 *
 * It deliberately carries no `predictive-search-products` id, so it is never mistaken for
 * real results by the code that decides whether previous results are worth keeping.
 *
 * @param {string} productsLabel
 * @returns {HTMLElement} a wrapper whose children replace the results container's
 */
function buildSkeleton(productsLabel) {
  const inner = document.createElement('div');
  inner.className = 'predictive-search-results__inner';
  inner.setAttribute('aria-hidden', 'true');

  const products = buildProductSkeleton(productsLabel);
  inner.append(buildSkeletonPills(), products);

  const wrapper = document.createElement('div');
  wrapper.append(inner);
  return wrapper;
}

/**
 * The placeholder row of suggested-search pills.
 *
 * Also used on its own by the first paint, which has real products but no pills yet.
 * @returns {HTMLElement}
 */
function buildSkeletonPills() {
  const pills = document.createElement('ul');
  pills.className =
    'predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled';
  pills.setAttribute('aria-hidden', 'true');

  for (let index = 0; index < SKELETON_PILLS; index++) {
    const item = document.createElement('li');
    item.className = 'predictive-search-results__card--query';
    const pill = document.createElement('span');
    pill.className = 'pills__pill predictive-search-results__pill kes-skeleton kes-skeleton--pill';
    // Varied widths so the row reads as words rather than as a bar chart.
    pill.style.setProperty('--kes-skeleton-width', `${60 + ((index * 37) % 70)}px`);
    item.append(pill);
    pills.append(item);
  }

  return pills;
}

/**
 * The placeholder product grid.
 * @param {string} productsLabel
 * @returns {HTMLElement}
 */
function buildProductSkeleton(productsLabel) {
  const products = document.createElement('div');
  products.className = 'predictive-search-results__products';

  const title = document.createElement('div');
  title.className = 'predictive-search-results__title h6';
  title.textContent = productsLabel;

  const list = document.createElement('ul');
  list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

  for (let index = 0; index < SKELETON_CARDS; index++) {
    const item = document.createElement('li');
    item.className = 'predictive-search-results__card predictive-search-results__card--product';
    item.innerHTML =
      '<div class="kes-skeleton kes-skeleton--card"></div>' +
      '<div class="kes-skeleton kes-skeleton--line"></div>' +
      '<div class="kes-skeleton kes-skeleton--line kes-skeleton--line-short"></div>';
    list.append(item);
  }

  products.append(title, list);
  return products;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** The last value searched for, so an unchanged value is not searched twice. */
  #lastSearchedTerm = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Safari has no window.requestIdleCallback, so this must be the guarded helper from
    // utilities (it falls back to setTimeout there). Calling the bare global threw a
    // ReferenceError on every page load once a shopper had viewed a product, which ended
    // connectedCallback here and left the placeholder typer below never started.
    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }

    this.#initPlaceholderTyper();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();

    /*
     * This used to bail out on any input event carrying no inputType, to ignore things
     * like the Escape key. That also silently dropped real input from browser autofill
     * and from some mobile keyboards, where nothing happened at all. Comparing the value
     * keeps the original intent (ignore events that did not change what was typed)
     * without discarding genuine ones.
     */
    if (searchTerm === this.#lastSearchedTerm) return;
    this.#lastSearchedTerm = searchTerm;

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    if (searchTerm.length < MIN_SEARCH_LENGTH) return;

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    // KES: Algolia picks the products and their order. Fired alongside Shopify's own
    // request rather than after it, so it costs no extra round trip. Returns null on any
    // failure, in which case Shopify's dropdown is used exactly as it is today.
    const algoliaProducts = window.KESAlgoliaPredictive
      ? window.KESAlgoliaPredictive.productItems(searchTerm, this.dataset.sectionId, abortController.signal)
      : Promise.resolve(null);

    /*
     * Two sources answer at very different speeds. Algolia returns in about 50ms and its
     * cards render in about 200ms; Shopify's predictive search takes 600 to 1800ms on any
     * prefix that is not already cached at the CDN, which is every prefix on the way to a
     * word. Waiting for both meant a shopper typing at normal speed saw the panel sit
     * completely still and then update once, nearly two seconds after the last keystroke.
     *
     * So the panel is painted twice: Algolia's products as soon as they are known, then
     * the full merged result when Shopify answers. Neither stage can undo the other, and
     * either can fail on its own without taking the dropdown down with it.
     */
    this.#setLoading(true);
    if (!this.#hasVisibleProducts()) {
      morph(predictiveSearchResults, buildSkeleton(this.#productsLabel));
    }

    let shopifyPainted = false;

    // Stage one: Algolia's products, at roughly 300ms.
    algoliaProducts
      .then((rendered) => {
        if (shopifyPainted || abortController.signal.aborted) return;
        if (!rendered?.shell || !rendered.items?.length) return;
        if (!isOnTopic(rendered.items, searchTerm)) return;

        // Cloned so the cards handed to stage two are never the ones morph has moved
        // into the live document.
        const panel = /** @type {HTMLElement} */ (rendered.shell.cloneNode(true));

        /*
         * This panel is products only: the suggested search pills come from Shopify and
         * are still in flight. Without a placeholder the real pills arrive a moment later
         * and shove every product card down the panel, so their space is held open.
         */
        const inner = panel.querySelector('.predictive-search-results__inner');
        if (inner && !inner.querySelector('.predictive-search-results__wrapper-queries')) {
          inner.prepend(buildSkeletonPills());
        }

        morph(predictiveSearchResults, panel);
        this.#resetScrollPositions();
      })
      .catch(() => {
        // Stage two still has this covered.
      });

    // Stage two: Shopify's suggested searches, collections, pages and articles, plus any
    // product it found that Algolia's index does not know about yet.
    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        let markup = resultsMarkup;
        const rendered = await algoliaProducts;
        const algoliaItems = rendered?.items;

        // Swap the cards only, leaving Shopify's own heading, suggested search pills,
        // collections, pages and articles exactly as they came back.
        //
        // Shopify's own cards are merged in, not thrown away. A product Shopify finds
        // that Algolia does not return is almost always newer than the Algolia index,
        // so dropping it made new products unfindable from the dropdown. Those extras go
        // after Algolia's cards, which keep their popularity order. When none of Algolia's
        // cards even contain the typed words, Algolia only found typo-tolerant near-misses
        // ('frog' -> 'frosted') and Shopify's answer is the right one, so the extras go first.
        if (algoliaItems?.length && !abortController.signal.aborted) {
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const list = doc.querySelector('#predictive-search-products ul');
          if (list) {
            // Cards carry no product id, so the product URL path is the identity.
            const productKey = (li) => li.querySelector('a[href]')?.getAttribute('href')?.split('?')[0] || '';
            const algoliaKeys = new Set(algoliaItems.map(productKey));
            const own = [...list.querySelectorAll(':scope > li')];
            const extras = own.filter((li) => !algoliaKeys.has(productKey(li)));
            const onTopic = isOnTopic(algoliaItems, searchTerm);
            const fromAlgolia = algoliaItems.map((li) => doc.importNode(li, true));
            list.replaceChildren(...(onTopic ? [...fromAlgolia, ...extras] : [...extras, ...fromAlgolia]));
            markup = doc.body.innerHTML;
          } else if (rendered.shell) {
            /*
             * Shopify found nothing at all, so its markup has no product list to merge
             * into and it is about to say 'No results found'. Algolia frequently does have
             * good answers here: Shopify's predictive search matches words in the title,
             * while Algolia is typo tolerant and searches more of the record, so a query
             * like 'aged brass ceiling pendant' returns nothing from Shopify and ten
             * correct pendants from Algolia. Showing 'No results found' on top of ten
             * real products is the worst answer available, so Algolia's panel is used
             * instead.
             *
             * Deliberately not gated on isOnTopic. That test decides whose results lead
             * when both have some; when Shopify has none there is nothing to lead, and
             * even a near-miss beats sending the shopper away empty handed.
             */
            markup = rendered.shell.cloneNode(true);
          }
        }

        if (abortController.signal.aborted) return;

        // Set before the morph, so a slow stage one landing later cannot overwrite the
        // fuller result with its products-only panel.
        shopifyPainted = true;
        this.#setLoading(false);

        morph(predictiveSearchResults, markup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        this.#setLoading(false);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * The theme's translated 'Products' heading, used for the skeleton so it does not
   * appear under whatever heading the previous panel happened to have.
   */
  get #productsLabel() {
    return this.dataset.productsLabel || 'Products';
  }

  /**
   * Are there real product results on screen right now? Recently viewed and featured
   * products from the empty state count, since keeping them beats a skeleton.
   * @returns {boolean}
   */
  #hasVisibleProducts() {
    return Boolean(this.refs.predictiveSearchResults.querySelector('#predictive-search-products li'));
  }

  /**
   * @param {boolean} isLoading - Whether a slower source is still answering.
   */
  #setLoading(isLoading) {
    this.refs.predictiveSearchResults.toggleAttribute('data-kes-loading', isLoading);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#lastSearchedTerm = '';
    this.#setLoading(false);
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };

  #initPlaceholderTyper() {
    const input = this.refs.searchInput;
    if (!input) return;

    const defaultPlaceholder = input.getAttribute('placeholder') || "Search for lights and more...";
    const phrases = [
      "What are you looking for?",
      "Search through more than 8,000 products here...",
      "Type your search criteria here"
    ];

    let phraseIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeoutId = null;
    let isFocused = false;

    const type = () => {
      if (isFocused || input.value !== '') {
        timeoutId = setTimeout(type, 500);
        return;
      }

      const currentPhrase = phrases[phraseIndex];
      
      if (isDeleting) {
        input.placeholder = currentPhrase.substring(0, charIndex - 1);
        charIndex--;
      } else {
        input.placeholder = currentPhrase.substring(0, charIndex + 1);
        charIndex++;
      }

      let delay = 50;

      if (!isDeleting && charIndex === currentPhrase.length) {
        delay = 3000;
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        delay = 500;
      } else if (isDeleting) {
        delay = 30;
      }

      timeoutId = setTimeout(type, delay);
    };

    const handleFocus = () => {
      isFocused = true;
      clearTimeout(timeoutId);
      input.placeholder = defaultPlaceholder;
    };

    const handleBlur = () => {
      isFocused = false;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(type, 3000);
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    timeoutId = setTimeout(type, 3000);

    this.#controller.signal.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    });
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
